import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ShieldCheck, FlaskConical, BadgeDollarSign } from "lucide-react";
import { ABOUT_IMG, AUTHOR_IMG, AUTHOR, AFFILIATE_URL } from "../lib/constants";
import { trackCta } from "../lib/tracking";

export default function About() {
  return (
    <main className="bg-white" data-testid="about-page">
      <Helmet>
        <title>About VoiceRanker.ai — Independent Editorial AI Voice Comparisons</title>
        <meta name="description" content="VoiceRanker.ai is an independent editorial comparison website for AI voice platforms. Learn how we evaluate products and maintain editorial independence." />
        <meta property="og:title" content="About VoiceRanker.ai — Independent Editorial AI Voice Comparisons" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://voiceranker.ai/about" />
        <link rel="canonical" href="https://voiceranker.ai/about" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">About us</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]" data-testid="about-title">
          Independent editorial comparisons. Transparent recommendations.
        </h1>
        <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed max-w-2xl">
          VoiceRanker.ai is an independent editorial comparison website dedicated to AI voice technology. Our goal is to help readers understand the strengths and differences between leading AI voice platforms by analysing publicly available product information, features, pricing, documentation, usability, market reputation and editorial experience where applicable. We do not accept payment in exchange for rankings or preferential placement. Some providers, including ElevenLabs, participate in affiliate programmes and we may earn a commission if you purchase through certain links on our website. These commercial relationships do not influence our editorial recommendations.
        </p>

        <img src={ABOUT_IMG} alt="Studio condenser microphone representing our AI voice editorial coverage" className="mt-12 w-full h-72 object-cover rounded-lg border border-slate-200" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
          {[
            [FlaskConical, "Editorial Evaluation", "We compare products using publicly available product information, feature analysis, pricing, documentation, usability, market reputation and editorial assessment where applicable."],
            [ShieldCheck, "No Paid Rankings", "Companies cannot purchase higher rankings on VoiceRanker.ai. Our recommendations are based on our editorial evaluation. Some providers may participate in affiliate programmes, but affiliate relationships never determine ranking positions."],
            [BadgeDollarSign, "Transparent Funding", "VoiceRanker.ai is reader-supported through affiliate partnerships. We currently maintain an affiliate relationship with ElevenLabs and may earn a commission if you sign up through qualifying links, at no additional cost to you. Affiliate relationships never influence our editorial recommendations."],
          ].map(([Icon, title, desc]) => (
            <div key={title} className="border border-slate-200 rounded-lg p-6">
              <Icon className="w-6 h-6 text-[#5A31F4]" />
              <h3 className="mt-4 font-heading font-bold text-[#0F172A]">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex items-center gap-4 bg-[#F8FAFC] border border-slate-200 rounded-lg p-6">
          <img src={AUTHOR_IMG} alt={AUTHOR} className="w-14 h-14 rounded-full object-cover border border-slate-200" />
          <div>
            <p className="font-heading font-bold text-[#0F172A]">{AUTHOR}</p>
            <p className="text-sm text-slate-600">Creators and developers who have shipped podcasts, YouTube channels and voice-enabled products with the tools we review.</p>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link to="/" data-testid="about-rankings-link" className="text-[#1A56DB] font-bold hover:underline">
            See our 2026 AI voice rankings →
          </Link>
          <p className="mt-4 text-sm text-slate-500">
            Or jump straight to our top recommendation:{" "}
            <a href={AFFILIATE_URL} target="_blank" rel="nofollow sponsored noopener" onClick={() => trackCta("about-page", AFFILIATE_URL)} data-testid="cta-about-elevenlabs" className="text-[#5A31F4] font-bold hover:underline">
              Try ElevenLabs Free →
            </a>
          </p>
          <p className="mt-2 text-xs text-slate-500 max-w-md mx-auto" data-testid="about-affiliate-disclosure">Affiliate Disclosure: If you sign up through this link, VoiceRanker.ai may earn a commission at no additional cost to you.</p>
        </div>
      </div>
    </main>
  );
}
